import { type Context, createContext, useCallback, useContext } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client/types';
// @ts-ignore - this is not a valid package entry point and cannot be resolved when using a modern Typescript 'moduleResolution' setting
import type { ExperimentCohortVariationControl } from '@atlassian/touchpoint-commons-event-analytics/src/common/types';

type ExperimentAttributes = {
	[key: string]: any;
};

export type ExperimentDictionary = {
	[key: string]: {
		cohort: ExperimentCohortVariationControl;
		timestamp: number;
	} & ExperimentAttributes;
};

export class ExperimentContextClass {
	private experimentDictionary: ExperimentDictionary = {};

	constructor(context?: ExperimentDictionary) {
		this.experimentDictionary = context ?? {};
	}

	experimentExposed(
		experimentName: string,
		experimentVariant: ExperimentCohortVariationControl,
		extraAttributes: ExperimentAttributes,
	): void {
		// If the experiment is already in the dictionary (i.e. been exposed before),
		// we don't want to overwrite the timestamp of first exposure.

		if (Object.keys(this.experimentDictionary).includes(experimentName)) {
			return;
		}

		this.experimentDictionary[experimentName] = {
			cohort: experimentVariant,
			timestamp: Date.now(),
			...extraAttributes,
		};
	}

	experiments(): ExperimentDictionary {
		return this.experimentDictionary;
	}
}

export const ExperimentContext: Context<ExperimentContextClass | null> =
	createContext<ExperimentContextClass | null>(null);

export function useExperimentContext(): ExperimentContextClass {
	const context = useContext(ExperimentContext);

	if (!context) {
		throw new Error('Missing context, please ensure this is used within ExperimentContext');
	}

	return context;
}

type GetExperimentValueParams = {
	experimentContextAttributes?: ExperimentAttributes;
	statsigParams: {
		defaultValue: ExperimentCohortVariationControl;
		experimentName: string;
		options?: GetExperimentValueOptions<ExperimentCohortVariationControl>;
		parameterName: string;
	};
};

/**
 * A helper hook which wraps the call to FeatureGates.getExperimentValue.
 * Returns a function that retrieves experiment values.
 *
 * If fireExperimentExposure is true, it will also update the experiment context.
 * By default, fireExperimentExposure is true if not provided.
 */
export const useGetExperimentValue = (): {
	getExperimentCohort: (
		experimentName: string,
		fireExposure?: boolean,
	) => ExperimentCohortVariationControl;
	getExperimentValue: (props: GetExperimentValueParams) => ExperimentCohortVariationControl;
} => {
	const experimentContext = useExperimentContext();

	const getExperimentValue: (props: GetExperimentValueParams) => ExperimentCohortVariationControl =
		useCallback(
			({
				statsigParams: { experimentName, parameterName, defaultValue, options },
				experimentContextAttributes = {},
			}): ExperimentCohortVariationControl => {
				const cohort = options
					? FeatureGates.getExperimentValue<ExperimentCohortVariationControl>(
							experimentName,
							parameterName,
							defaultValue,
							options,
						)
					: FeatureGates.getExperimentValue<ExperimentCohortVariationControl>(
							experimentName,
							parameterName,
							defaultValue,
						);

				// By default, fireExperimentExposure is true but we need to explicitly
				// check for that to ensure we fire our context update correctly.
				if (options?.fireExperimentExposure !== false) {
					experimentContext.experimentExposed(experimentName, cohort, experimentContextAttributes);
				}
				return cohort;
			},
			[experimentContext],
		);

	const getExperimentCohort = useCallback(
		(experimentName: string, fireExposure = true): ExperimentCohortVariationControl =>
			getExperimentValue({
				statsigParams: {
					experimentName,
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
					options: fireExposure ? undefined : { fireExperimentExposure: false },
				},
			}),
		[getExperimentValue],
	);

	return { getExperimentValue, getExperimentCohort };
};
